export const onLine = (x1, y1, x2, y2, x, y, maxDistance = 1) => {
  const a = { x: x1, y: y1 };
  const b = { x: x2, y: y2 };
  const c = { x, y };
  const offset = distance(a, b) - (distance(a, c) + distance(b, c));
  return Math.abs(offset) < maxDistance ? "inside" : null;
};

export const nearPoint = (x, y, x1, y1, name) => {
  return Math.abs(x - x1) < 5 && Math.abs(y - y1) < 5 ? name : null;
};

// Helper function to rotate a point around a center
const rotatePoint = (pointX, pointY, centerX, centerY, angle) => {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const translatedX = pointX - centerX;
  const translatedY = pointY - centerY;
  return {
    x: translatedX * cos - translatedY * sin + centerX,
    y: translatedX * sin + translatedY * cos + centerY
  };
};

// Helper function to check if point is inside rotated rectangle using winding number
const isPointInRotatedRect = (px, py, x1, y1, x2, y2, rotation = 0) => {
  if (rotation === 0) {
    return px >= x1 && px <= x2 && py >= y1 && py <= y2;
  }

  // For rotated rectangles, get corners and use point-in-polygon test
  const corners = getRectangleCorners({ x1, y1, x2, y2, rotation });
  return isPointInPolygon(px, py, corners);
};

// Point-in-polygon test using ray casting algorithm
const isPointInPolygon = (px, py, corners) => {
  let inside = false;

  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const xi = corners[i].x;
    const yi = corners[i].y;
    const xj = corners[j].x;
    const yj = corners[j].y;

    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
};

export const positionWithinElement = (x, y, element) => {
  const { type, x1, x2, y1, y2, rotation = 0 } = element;
  switch (type) {
    case "line":
      const on = onLine(x1, y1, x2, y2, x, y);
      const start = nearPoint(x, y, x1, y1, "start");
      const end = nearPoint(x, y, x2, y2, "end");
      return start || end || on;
    case "rectangle":
      if (rotation === 0) {
        const topLeft = nearPoint(x, y, x1, y1, "tl");
        const topRight = nearPoint(x, y, x2, y1, "tr");
        const bottomLeft = nearPoint(x, y, x1, y2, "bl");
        const bottomRight = nearPoint(x, y, x2, y2, "br");
        const inside = x >= x1 && x <= x2 && y >= y1 && y <= y2 ? "inside" : null;
        if (inside) return inside;

        // check if cursor is on any of the edge
        const topEdge = onLine(x1, y1, x2, y1, x, y) ? "top" : null;
        const bottomEdge = onLine(x1, y2, x2, y2, x, y) ? "bottom" : null;
        const leftEdge = onLine(x1, y1, x1, y2, x, y) ? "left" : null;
        const rightEdge = onLine(x2, y1, x2, y2, x, y) ? "right" : null;
        const onEdge = topEdge || bottomEdge || leftEdge || rightEdge;

        return topLeft || topRight || bottomLeft || bottomRight || onEdge;
      } else {
        // For rotated rectangles, check corners and edges in rotated space
        const corners = getRectangleCorners(element);

        // Check corner proximity
        for (let i = 0; i < corners.length; i++) {
          const corner = corners[i];
          if (Math.abs(x - corner.x) < 5 && Math.abs(y - corner.y) < 5) {
            // Return appropriate corner position based on rotation
            const cornerNames = ['tl', 'tr', 'br', 'bl'];
            return cornerNames[i];
          }
        }

        // Check if point is inside rotated rectangle
        const inside = isPointInRotatedRect(x, y, x1, y1, x2, y2, rotation) ? "inside" : null;
        if (inside) return inside;

        // Check edges for rotated rectangles
        const rotatedCorners = getRectangleCorners(element);
        const edgeTolerance = 5; // Pixels

        // Top edge (between top-left and top-right rotated corners)
        const topEdge = onLine(rotatedCorners[0].x, rotatedCorners[0].y, rotatedCorners[1].x, rotatedCorners[1].y, x, y, edgeTolerance) ? "top" : null;
        // Right edge (between top-right and bottom-right rotated corners)
        const rightEdge = onLine(rotatedCorners[1].x, rotatedCorners[1].y, rotatedCorners[2].x, rotatedCorners[2].y, x, y, edgeTolerance) ? "right" : null;
        // Bottom edge (between bottom-right and bottom-left rotated corners)
        const bottomEdge = onLine(rotatedCorners[2].x, rotatedCorners[2].y, rotatedCorners[3].x, rotatedCorners[3].y, x, y, edgeTolerance) ? "bottom" : null;
        // Left edge (between bottom-left and top-left rotated corners)
        const leftEdge = onLine(rotatedCorners[3].x, rotatedCorners[3].y, rotatedCorners[0].x, rotatedCorners[0].y, x, y, edgeTolerance) ? "left" : null;

        const onEdge = topEdge || rightEdge || bottomEdge || leftEdge;

        return onEdge;
      }
    case "pencil":
      const betweenAnyPoint = element.points.some((point, index) => {
        const nextPoint = element.points[index + 1];
        if (!nextPoint) return false;
        return onLine(point.x, point.y, nextPoint.x, nextPoint.y, x, y, 5) != null;
      });
      return betweenAnyPoint ? "inside" : null;
    case "text":
      return isPointInRotatedRect(x, y, x1, y1, x2, y2, rotation) ? "inside" : null;
    default:
      throw new Error(`Type not recognised: ${type}`);
  }
};

export const distance = (a, b) => Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));

export const getElementAtPosition = (x, y, elements) => {
  const elementsWithin = elements.map((element) => ({ ...element, position: positionWithinElement(x, y, element) }));
  return elementsWithin
    .slice()
    .sort((a, b) => b.zIndex - a.zIndex)
    .find((element) => element.position !== null);
};

export const adjustmentRequired = (type) => ["line", "rectangle"].includes(type);

export const adjustElementCoordinates = (element) => {
  try {
    if (!element) return null;
    const { type, x1, y1, x2, y2 } = element;
    if (type === "rectangle") {
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);
      return { x1: minX, y1: minY, x2: maxX, y2: maxY };
    } else {
      if (x1 < x2 || (x1 === x2 && y1 < y2)) {
        return { x1, y1, x2, y2 };
      } else {
        return { x1: x2, y1: y2, x2: x1, y2: y1 };
      }
    }
  } catch (error) {
    console.error("Error adjusting element coordinates:", error);
    return null;
  }
};

export const cursorForPosition = (position) => {
  switch (position) {
    case "tl":
    case "br":
    case "start":
    case "end":
      return "nwse-resize";
    case "tr":
    case "bl":
      return "nesw-resize";
    case "top":
    case "bottom":
      return "ns-resize";
    case "left":
    case "right":
      return "ew-resize";
    default:
      return "move";
  }
};

export const resizedCoordinates = (clientX, clientY, position, coordinates) => {
  const { x1, y1, x2, y2 } = coordinates;
  switch (position) {
    case "tl":
    case "start":
      return { x1: clientX, y1: clientY, x2, y2 };
    case "tr":
      return { x1, y1: clientY, x2: clientX, y2 };
    case "bl":
      return { x1: clientX, y1, x2, y2: clientY };
    case "br":
    case "end":
      return { x1, y1, x2: clientX, y2: clientY };
    case "top":
      return { x1, y1: clientY, x2, y2 };
    case "bottom":
      return { x1, y1, x2, y2: clientY };
    case "left":
      return { x1: clientX, y1, x2, y2 };
    case "right":
      return { x1, y1, x2: clientX, y2 };
    default:
      return null; //should not really get here...
  }
};

export const resizeElement = (clientX, clientY, element, handlePosition) => {

  // --- WALL LOGIC ---
  if (element.objectType === 'wall') {
    // Only allow handles at endpoints ("start" and "end" or "n" and "s")
    if (handlePosition === "n" || handlePosition === "start" || handlePosition === "tl" || handlePosition === "top" || handlePosition === "left" || handlePosition === "tr") {
      // Move the first endpoint (x1,y1)
      return { ...element, x1: clientX, y1: clientY };
    }
    if (handlePosition === "s" || handlePosition === "end" || handlePosition === "bl" || handlePosition === "br") {
      // Move the second endpoint (x2,y2)
      return { ...element, x2: clientX, y2: clientY };
    }
    // If not at an endpoint, do not resize (ignore)
    return element;
  }

  const { x1, y1, x2, y2, rotation = 0 } = element;
  const corners = getRectangleCorners(element);

  // Corner order from getRectangleCorners: 0:tl, 1:tr, 2:br, 3:bl
  const pivotMap = {
    tl: corners[2], // pivot is br
    tr: corners[3], // pivot is bl
    br: corners[0], // pivot is tl
    bl: corners[1], // pivot is tr
  };

  const mousePos = { x: clientX, y: clientY };

  if (pivotMap[handlePosition]) {
    // It's a corner resize
    const pivot = pivotMap[handlePosition];
    const diagonal = { x: mousePos.x - pivot.x, y: mousePos.y - pivot.y };

    const angleRad = (rotation * Math.PI) / 180;
    const cosA = Math.cos(angleRad);
    const sinA = Math.sin(angleRad);

    // Project the diagonal onto the element's local axes to find new width and height
    const newWidth = diagonal.x * cosA + diagonal.y * sinA;
    const newHeight = diagonal.y * cosA - diagonal.x * sinA;

    // The new center is the pivot + half the diagonal vector
    const newCenterX = pivot.x + diagonal.x / 2;
    const newCenterY = pivot.y + diagonal.y / 2;

    // Calculate new un-rotated bounding box
    const finalX1 = newCenterX - Math.abs(newWidth) / 2;
    const finalY1 = newCenterY - Math.abs(newHeight) / 2;

    return {
      ...element,
      x1: finalX1,
      y1: finalY1,
      x2: finalX1 + Math.abs(newWidth),
      y2: finalY1 + Math.abs(newHeight),
    };
  }

  // It's an edge resize
  const angleRad = (rotation * Math.PI) / 180;
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);
  let newElement = { ...element };

  switch (handlePosition) {
    case "n":
    case "top": {
      const bottomEdgeCenter = { x: (corners[2].x + corners[3].x) / 2, y: (corners[2].y + corners[3].y) / 2 };
      const vecToMouse = { x: mousePos.x - bottomEdgeCenter.x, y: mousePos.y - bottomEdgeCenter.y };
      const newHeight = vecToMouse.y * cosA - vecToMouse.x * sinA;
      const width = x2 - x1;
      const heightVec = { x: -newHeight * sinA, y: newHeight * cosA };
      const newCenter = { x: bottomEdgeCenter.x + heightVec.x / 2, y: bottomEdgeCenter.y + heightVec.y / 2 };
      newElement.x1 = newCenter.x - width / 2;
      newElement.y1 = newCenter.y - Math.abs(newHeight) / 2;
      newElement.x2 = newCenter.x + width / 2;
      newElement.y2 = newCenter.y + Math.abs(newHeight) / 2;
      newElement.width = width;
      newElement.height = Math.abs(newHeight);
      break;
    }
    case "s":
    case "bottom": {
      const topEdgeCenter = { x: (corners[0].x + corners[1].x) / 2, y: (corners[0].y + corners[1].y) / 2 };
      const vecToMouse = { x: mousePos.x - topEdgeCenter.x, y: mousePos.y - topEdgeCenter.y };
      const newHeight = vecToMouse.y * cosA - vecToMouse.x * sinA;
      const width = x2 - x1;
      const heightVec = { x: -newHeight * sinA, y: newHeight * cosA };
      const newCenter = { x: topEdgeCenter.x + heightVec.x / 2, y: topEdgeCenter.y + heightVec.y / 2 };
      newElement.x1 = newCenter.x - width / 2;
      newElement.y1 = newCenter.y - Math.abs(newHeight) / 2;
      newElement.x2 = newCenter.x + width / 2;
      newElement.y2 = newCenter.y + Math.abs(newHeight) / 2;
      newElement.width = width;
      newElement.height = Math.abs(newHeight);
      break;
    }
    case "w":
    case "left": {
      const rightEdgeCenter = { x: (corners[1].x + corners[2].x) / 2, y: (corners[1].y + corners[2].y) / 2 };
      const vecToMouse = { x: mousePos.x - rightEdgeCenter.x, y: mousePos.y - rightEdgeCenter.y };
      const newWidth = vecToMouse.x * cosA + vecToMouse.y * sinA;
      const height = y2 - y1;
      const widthVec = { x: newWidth * cosA, y: newWidth * sinA };
      const newCenter = { x: rightEdgeCenter.x + widthVec.x / 2, y: rightEdgeCenter.y + widthVec.y / 2 };
      newElement.x1 = newCenter.x - Math.abs(newWidth) / 2;
      newElement.y1 = newCenter.y - height / 2;
      newElement.x2 = newCenter.x + Math.abs(newWidth) / 2;
      newElement.y2 = newCenter.y + height / 2;
      newElement.width = Math.abs(newWidth);
      newElement.height = height;
      break;
    }
    case "e":
    case "right": {
      const leftEdgeCenter = { x: (corners[0].x + corners[3].x) / 2, y: (corners[0].y + corners[3].y) / 2 };
      const vecToMouse = { x: mousePos.x - leftEdgeCenter.x, y: mousePos.y - leftEdgeCenter.y };
      const newWidth = vecToMouse.x * cosA + vecToMouse.y * sinA;
      const height = y2 - y1;
      const widthVec = { x: newWidth * cosA, y: newWidth * sinA };
      const newCenter = { x: leftEdgeCenter.x + widthVec.x / 2, y: leftEdgeCenter.y + widthVec.y / 2 };
      newElement.x1 = newCenter.x - Math.abs(newWidth) / 2;
      newElement.y1 = newCenter.y - height / 2;
      newElement.x2 = newCenter.x + Math.abs(newWidth) / 2;
      newElement.y2 = newCenter.y + height / 2;
      newElement.width = Math.abs(newWidth);
      newElement.height = height;
      break;
    }
    default:
      return element;
  }
  return newElement;
};

// TODO: To be removed
export const isColliding1 = (rectAOriginal, rectBOriginal) => {
  const rectA = { ...rectAOriginal };
  const rectB = { ...rectBOriginal };
  rectA.x = Math.min(rectA.x1, rectA.x2);
  rectA.y = Math.min(rectA.y1, rectA.y2);
  rectA.width = Math.abs(rectA.x2 - rectA.x1);
  rectA.height = Math.abs(rectA.y2 - rectA.y1);

  rectB.x = Math.min(rectB.x1, rectB.x2);
  rectB.y = Math.min(rectB.y1, rectB.y2);
  rectB.width = Math.abs(rectB.x2 - rectB.x1);
  rectB.height = Math.abs(rectB.y2 - rectB.y1);

  return !(rectA.x + rectA.width < rectB.x || rectB.x + rectB.width < rectA.x || rectA.y + rectA.height < rectB.y || rectB.y + rectB.height < rectA.y);
};

const normalizeRect = (rect) => {
  return {
    x1: Math.min(rect.x1, rect.x2),
    y1: Math.min(rect.y1, rect.y2),
    x2: Math.max(rect.x1, rect.x2),
    y2: Math.max(rect.y1, rect.y2),
    width: Math.abs(rect.x2 - rect.x1),
    height: Math.abs(rect.y2 - rect.y1)
  };
};

// Get corners of a rectangle (considering rotation)
const getRectangleCorners = (rect) => {
  const { x1, y1, x2, y2, rotation = 0, thickness = 10 } = rect;

  // Case 1: It's a normal axis-aligned or rotated box
  const centerX = (x1 + x2) / 2;
  const centerY = (y1 + y2) / 2;

  let width = Math.abs(x2 - x1);
  let height = Math.abs(y2 - y1);
  let baseRotation = rotation;

  // Case 2: If this is a wall defined by endpoints (zero height or width), calculate differently
  if (rect.objectType === 'wall' && (width === 0 || height === 0)) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);

    width = length;          // actual wall length
    height = thickness;      // wall’s thickness
    baseRotation = (Math.atan2(dy, dx) * 180) / Math.PI; // align along segment
  }

  // If no rotation, return axis-aligned corners
  if (baseRotation === 0) {
    return [
      { x: centerX - width / 2, y: centerY - height / 2 },
      { x: centerX + width / 2, y: centerY - height / 2 },
      { x: centerX + width / 2, y: centerY + height / 2 },
      { x: centerX - width / 2, y: centerY + height / 2 }
    ];
  }

  // Rotated rectangle corners calculation
  const radians = (baseRotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const localCorners = [
    { x: -width / 2, y: -height / 2 },
    { x: width / 2, y: -height / 2 },
    { x: width / 2, y: height / 2 },
    { x: -width / 2, y: height / 2 }
  ];

  return localCorners.map(corner => ({
    x: centerX + corner.x * cos - corner.y * sin,
    y: centerY + corner.x * sin + corner.y * cos
  }));
};


// Separating Axis Theorem (SAT) for oriented bounding box collision
const satCollision = (rectA, rectB) => {
  const cornersA = getRectangleCorners(rectA);
  const cornersB = getRectangleCorners(rectB);

  // Get all potential separating axes (perpendicular to each edge)
  const axes = [];

  // Add axes from rectangle A
  for (let i = 0; i < cornersA.length; i++) {
    const j = (i + 1) % cornersA.length;
    const edge = {
      x: cornersA[j].x - cornersA[i].x,
      y: cornersA[j].y - cornersA[i].y
    };
    // Perpendicular vector (normal)
    axes.push({ x: -edge.y, y: edge.x });
  }

  // Add axes from rectangle B
  for (let i = 0; i < cornersB.length; i++) {
    const j = (i + 1) % cornersB.length;
    const edge = {
      x: cornersB[j].x - cornersB[i].x,
      y: cornersB[j].y - cornersB[i].y
    };
    // Perpendicular vector (normal)
    axes.push({ x: -edge.y, y: edge.x });
  }

  // Test each axis
  for (const axis of axes) {
    // Normalize the axis
    const length = Math.sqrt(axis.x * axis.x + axis.y * axis.y);
    if (length === 0) continue;
    const normalizedAxis = { x: axis.x / length, y: axis.y / length };

    // Project both rectangles onto this axis
    const projectionA = projectPolygon(cornersA, normalizedAxis);
    const projectionB = projectPolygon(cornersB, normalizedAxis);

    // Check if projections overlap
    if (projectionA.max < projectionB.min || projectionB.max < projectionA.min) {
      // Found separating axis - no collision
      return false;
    }
  }

  // No separating axis found - collision detected
  return true;
};

// Project polygon onto an axis
const projectPolygon = (corners, axis) => {
  let min = corners[0].x * axis.x + corners[0].y * axis.y;
  let max = min;

  for (let i = 1; i < corners.length; i++) {
    const projection = corners[i].x * axis.x + corners[i].y * axis.y;
    if (projection < min) min = projection;
    if (projection > max) max = projection;
  }

  return { min, max };
};

// Improved collision detection with proper rotation support
export const isColliding = (rectA, rectB) => {
  const a = normalizeRect(rectA);
  const b = normalizeRect(rectB);

  // Helper: Check if both have no rotation => Use AABB
  const useAABB = (!rectA.rotation || rectA.rotation === 0) && (!rectB.rotation || rectB.rotation === 0);

  // ✅ CASE 1: Both are NOT walls
  if (rectA.objectType !== 'wall' && rectB.objectType !== 'wall') {
    if (useAABB) {
      return !(
        a.x2 <= b.x1 || // A is completely left of B
        a.x1 >= b.x2 || // A is completely right of B
        a.y2 <= b.y1 || // A is completely above B
        a.y1 >= b.y2    // A is completely below B
      );
    }
    return satCollision(rectA, rectB);

  }

  // ✅ CASE 2: One is wall, one is not
  if (rectA.objectType === 'wall' && rectB.objectType !== 'wall') {
    return false; // ignore collisions between walls and non-walls
    // return satCollision(rectA, rectB);
  }
  if (rectA.objectType !== 'wall' && rectB.objectType === 'wall') {
    return false; // ignore collisions between walls and non-walls
    // return satCollision(rectA, rectB);
  }

  // ✅ CASE 3: Both are walls
  // Un-comment this to use SAT collision detection for walls
  // return satCollision(rectA, rectB);
  return false;
};

export const getMouseCoordinates = ({ event, zoom, panOffset }) => {
  const boundingRect = event.target.getBoundingClientRect();

  const clientX = (event.clientX - boundingRect.left - panOffset.x) / zoom;
  const clientY = (event.clientY - boundingRect.top - panOffset.y) / zoom;

  const orinalX = event.clientX - boundingRect.left;
  const orinalY = event.clientY - boundingRect.top;

  return { orinalX, orinalY, clientX, clientY, boundingRect };
};

// Export the helper functions for use in other modules
export { rotatePoint, isPointInRotatedRect, getRectangleCorners, satCollision };

// Helper function to get expanded bounding box for rotated rectangle (for performance optimization)
export const getRotatedBoundingBox = (rect) => {
  if (!rect.rotation || rect.rotation === 0) {
    return normalizeRect(rect);
  }

  const corners = getRectangleCorners(rect);
  let minX = corners[0].x;
  let maxX = corners[0].x;
  let minY = corners[0].y;
  let maxY = corners[0].y;

  for (const corner of corners) {
    if (corner.x < minX) minX = corner.x;
    if (corner.x > maxX) maxX = corner.x;
    if (corner.y < minY) minY = corner.y;
    if (corner.y > maxY) maxY = corner.y;
  }

  return {
    x1: minX,
    y1: minY,
    x2: maxX,
    y2: maxY,
    width: maxX - minX,
    height: maxY - minY
  };
};
